import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Trash2, KeyRound, UserPlus, ShieldCheck, ScrollText, Users as UsersIcon, Beaker } from "lucide-react";
import { fmtLocalDTFull } from "@/lib/clinical";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import MfaCard from "@/components/MfaCard";
import AdminPatients from "@/components/AdminPatients";
import DemoModeCard from "@/components/DemoModeCard";

export default function Admin() {
  const [users, setUsers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [open, setOpen] = useState(false);
  const [resetting, setResetting] = useState(null);
  const [form, setForm] = useState({ username: "", full_name: "", password: "", role: "doctor", consultant: "" });
  const [pw, setPw] = useState("");

  const load = async () => {
    try {
      const [{ data: u }, { data: l }] = await Promise.all([api.get("/users"), api.get("/users/audit-logs")]);
      setUsers(u); setLogs(l);
    } catch { toast.error("Failed to load"); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await api.post("/users", form);
      toast.success("User created");
      setOpen(false);
      setForm({ username: "", full_name: "", password: "", role: "doctor", consultant: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const toggle = async (u) => {
    await api.patch(`/users/${u.id}`, { active: !u.active });
    load();
  };

  const del = async (u) => {
    if (!window.confirm(`Delete ${u.username}?`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
  };

  const doReset = async () => {
    if (!pw || pw.length < 6) return toast.error("Password too short");
    try { await api.post(`/users/${resetting.id}/reset-password`, { new_password: pw }); toast.success("Password reset"); setResetting(null); setPw(""); }
    catch { toast.error("Failed"); }
  };

  return (
    <div className="space-y-6" data-testid="admin-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground">Administrator</div>
          <h1 className="font-display text-3xl font-semibold mt-1">System control</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage users, roles and audit trail.</p>
        </div>
        <Button className="rounded-full" onClick={() => setOpen(true)} data-testid="btn-new-user"><UserPlus size={16} className="mr-2" /> New user</Button>
      </div>

      <Tabs defaultValue="users">
        <TabsList className="bg-transparent border-b border-border rounded-none p-0 h-auto">
          <TabsTrigger value="users" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"><ShieldCheck size={14} className="mr-2" /> Users</TabsTrigger>
          <TabsTrigger value="patients" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"><UsersIcon size={14} className="mr-2" /> Patients</TabsTrigger>
          <TabsTrigger value="security" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"><KeyRound size={14} className="mr-2" /> Security</TabsTrigger>
          <TabsTrigger value="training" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"><Beaker size={14} className="mr-2" /> Training</TabsTrigger>
          <TabsTrigger value="audit" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"><ScrollText size={14} className="mr-2" /> Audit log</TabsTrigger>
        </TabsList>

        <TabsContent value="users" className="pt-6">
          <div className="card-clinical overflow-hidden">
            <div className="grid grid-cols-12 px-4 py-2 border-b border-border text-[11px] uppercase tracking-wider text-muted-foreground">
              <div className="col-span-3">Username</div><div className="col-span-3">Full name</div><div className="col-span-2">Role</div>
              <div className="col-span-2">Consultant</div><div className="col-span-1">Status</div><div className="col-span-1 text-right">Actions</div>
            </div>
            {users.map(u => (
              <div key={u.id} className="grid grid-cols-12 px-4 py-3 border-b border-border/60 items-center text-sm">
                <div className="col-span-3 font-mono text-xs">{u.username}</div>
                <div className="col-span-3">{u.full_name}</div>
                <div className="col-span-2 capitalize">{u.role}</div>
                <div className="col-span-2 text-muted-foreground">{u.consultant || "—"}</div>
                <div className="col-span-1">
                  <button onClick={() => toggle(u)} className={`text-[10px] rounded-full px-2 py-0.5 ${u.active ? "bg-vacant" : "bg-muted text-muted-foreground"}`}>
                    {u.active ? "active" : "inactive"}
                  </button>
                </div>
                <div className="col-span-1 text-right flex justify-end gap-2">
                  <Button size="sm" variant="outline" className="h-7 rounded-full text-xs" onClick={() => setResetting(u)}><KeyRound size={12} /></Button>
                  <Button size="sm" variant="outline" className="h-7 rounded-full text-xs" onClick={() => del(u)}><Trash2 size={12} /></Button>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="security" className="pt-6 space-y-4">
          <MfaCard />
          <div className="card-clinical p-5">
            <div className="font-medium">Session security</div>
            <div className="text-xs text-muted-foreground mt-1">
              Tokens auto-expire after 12 hours. Rotate the prototype <span className="font-mono">admin</span> password from the Users list before clinical use.
            </div>
          </div>
        </TabsContent>

        <TabsContent value="patients" className="pt-6">
          <AdminPatients />
        </TabsContent>

        <TabsContent value="training" className="pt-6">
          <DemoModeCard />
        </TabsContent>

        <TabsContent value="audit" className="pt-6">
          <div className="card-clinical overflow-hidden">
            <div className="grid grid-cols-12 px-4 py-2 border-b border-border text-[11px] uppercase tracking-wider text-muted-foreground">
              <div className="col-span-3">When</div><div className="col-span-2">User</div><div className="col-span-2">Role</div>
              <div className="col-span-2">Action</div><div className="col-span-3">Details</div>
            </div>
            {logs.length === 0 && <div className="p-6 text-sm text-muted-foreground text-center">No activity yet</div>}
            {logs.map((l) => (
              <div key={l.id} className="grid grid-cols-12 px-4 py-3 border-b border-border/60 items-center text-xs">
                <div className="col-span-3 font-mono">{fmtLocalDTFull(l.at)}</div>
                <div className="col-span-2">{l.username}</div>
                <div className="col-span-2 capitalize">{l.role}</div>
                <div className="col-span-2"><Badge variant="secondary" className="rounded-full text-[10px]">{l.action}</Badge></div>
                <div className="col-span-3 truncate text-muted-foreground">{JSON.stringify(l.details)}</div>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="new-user-dialog">
          <DialogHeader><DialogTitle>Create user</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Username</Label><Input value={form.username} onChange={(e) => setForm(s => ({ ...s, username: e.target.value }))} data-testid="new-user-username" /></div>
            <div><Label>Full name</Label><Input value={form.full_name} onChange={(e) => setForm(s => ({ ...s, full_name: e.target.value }))} /></div>
            <div className="col-span-2"><Label>Password</Label><Input type="password" value={form.password} onChange={(e) => setForm(s => ({ ...s, password: e.target.value }))} /></div>
            <div><Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm(s => ({ ...s, role: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{["doctor", "nurse", "receptionist", "admin"].map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Consultant (doctor)</Label><Input value={form.consultant} onChange={(e) => setForm(s => ({ ...s, consultant: e.target.value }))} /></div>
          </div>
          <DialogFooter><Button className="rounded-full" onClick={create} data-testid="new-user-submit">Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!resetting} onOpenChange={(o) => !o && setResetting(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reset password — {resetting?.username}</DialogTitle></DialogHeader>
          <Label>New password</Label>
          <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} />
          <DialogFooter><Button className="rounded-full" onClick={doReset}>Reset</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
